import { i18n } from '@lingui/core';
import { Trans } from '@lingui/react';
import clsx from 'clsx';
import connectStats from 'instantsearch.js/es/connectors/stats/connectStats';
import React from 'react';
import type { CurrentRefinementsProps } from 'react-instantsearch';
import {
  useConnector,
  ClearRefinements,
  useCurrentRefinements,
} from 'react-instantsearch';
import IconClose from 'icons/misc/close.svg';
import styles from './ActiveFilters.module.scss';

export const ActiveFilters = ({
  showClearRefinements = true,
  showCount = false,
  className,
}: {
  showClearRefinements?: boolean;
  showCount?: boolean;
  className?: string;
}) => {
  return (
    <CustomCurrentRefinements
      showClearRefinements={showClearRefinements}
      showCount={showCount}
      className={className}
    />
  );
};

const CustomCurrentRefinements = (
  props: CurrentRefinementsProps & {
    showClearRefinements: boolean;
    showCount?: boolean;
  }
) => {
  const { items, refine } = useCurrentRefinements(props);

  if (!items.length) {
    return null;
  }

  return (
    <div className={styles.activeFilters__wrapper}>
      {props.showCount && (
        <div className={styles.activeFilters__countWrapper}>
          <ResultsCount />
        </div>
      )}

      <div className={clsx(styles.activeFilters, props.className)}>
        <ul className={styles.currentRefinement__list}>
          {items.map((item) => {
            return item.refinements.map((refinement) => (
              <li key={refinement.value}>
                <button
                  className={styles.currentRefinement__button}
                  onClick={() => refine(refinement)}
                  aria-label={i18n._(
                    /* i18n */
                    'Remove applied filter: {value}',
                    {
                      value: refinement.label,
                    }
                  )}
                >
                  <span
                    className={clsx(
                      styles.currentRefinement__label,
                      'text-utility-utility-small-mobile',
                      'text-utility-utility-small-desktop'
                    )}
                  >
                    {refinement.label}
                  </span>
                  <IconClose />
                </button>
              </li>
            ));
          })}
        </ul>

        {props.showClearRefinements && (
          <ClearRefinements
            classNames={{
              root: styles.activeFilters__clearRefinements,
              button: styles.activeFilters__clearRefinementsButton,
              disabledButton:
                styles['activeFilters__clearRefinementsButton--disabled'],
            }}
            translations={{
              resetButtonText: i18n._(
                /* i18n */ {
                  id: 'filters.clearAll',
                  message: 'Clear All',
                }
              ),
            }}
          />
        )}
      </div>
    </div>
  );
};

type UseStatsOutput = {
  hitsPerPage: number;
  nbHits: number;
  areHitsSorted: boolean;
  nbSortedHits: number;
  nbPages: number;
  page: number;
  processingTimeMS: number;
  query: string;
};

function useStats() {
  return useConnector(connectStats) as UseStatsOutput;
}

const ResultsCount = () => {
  const stats = useStats();

  const { nbHits: totalItems } = stats;
  return (
    <p
      className={clsx(
        styles.activeFilters__count,
        'text-utility-utility-small'
      )}
    >
      <Trans
        id="filters.itemsCount"
        message="{count} items"
        values={{ count: totalItems }}
      />
    </p>
  );
};
